// ═══════════════════════════════════════════════════════════════════════════
// ADMIN: RISK LIST DETAIL
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Note de naming : `pages.adminRiskLists` (root listing) est défini dans
// page-admin-committees, ici on ne traite que le détail d'une liste.

import { definePage } from '../router.js';
import { wireBulkStageBar } from './page-risklists-bulk-bar.js';
import { buildStdRiskRow, buildStageCheckboxes } from './page-risklists-fragments.js';

export interface StdRisk {
  id: number;
  category?: string | null;
  visibility: string;
  cause?: string | null;
  consequence?: string | null;
  stages?: string[];
}

interface RiskListDetailData {
  id: number;
  name: string;
  description?: string | null;
  risks: StdRisk[];
}

export interface StageRow {
  id: number;
  name: string;
}

type RiskListSortKey = 'category' | 'stages' | 'visibility' | 'cause' | 'consequence';

declare global {
  interface Window {
    renderRiskListDetail: typeof renderRiskListDetail;
    showStdRiskModal: typeof showStdRiskModal;
  }
}

pages.adminRiskListDetail = definePage<'admin/risk-lists/:id'>(async ({ id }) => {
  const lid = id;
  setHeader(t('common.loading'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [listData, stages] = await Promise.all([
      api.getRiskList(lid) as Promise<RiskListDetailData>,
      loadStages() as Promise<StageRow[]>,
    ]);
    renderRiskListDetail(listData, stages);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

function renderRiskListDetail(listData: RiskListDetailData, stages: ReadonlyArray<StageRow>): void {
  const lid = listData.id;
  // `risk_lists.read` garde la route, mais c'est une clé de menu : elle
  // n'autorise pas l'écriture (revue front §I3). Les deux exports restent
  // offerts — ce sont des lectures.
  const canWrite = !!(window.hasPermission && window.hasPermission('risk_lists.write'));
  setHeader(
    `<a href="#admin/risk-lists" style="color:var(--accent);text-decoration:underline">${esc(t('risk_lists.parent_title'))}</a> / ${esc(listData.name)}`,
    `<a href="${api.exportRisksUrl(lid)}?fmt=json" download class="btn btn-secondary ml-auto" id="btn-export-rl-json">${esc(t('risk_lists.btn.export_json'))}</a>
     <a href="${api.exportRisksUrl(lid)}?fmt=csv" download class="btn btn-secondary" style="margin-left:8px">${esc(t('risk_lists.btn.export_csv'))}</a>` +
      (canWrite
        ? `<button class="btn btn-primary" id="btn-add-std-risk" style="margin-left:8px">${esc(t('common.add'))}</button>`
        : ''),
  );

  const risks = listData.risks || [];

  let rlSortKey: RiskListSortKey = 'category';
  let rlSortDir: 'asc' | 'desc' = 'asc';

  const sortRisks = (arr: ReadonlyArray<StdRisk>): StdRisk[] =>
    [...arr].sort((a, b) => {
      let va: string;
      let vb: string;
      switch (rlSortKey) {
        case 'stages':
          va = (a.stages || []).join(',');
          vb = (b.stages || []).join(',');
          break;
        case 'category':
          va = (a.category || '').toLowerCase();
          vb = (b.category || '').toLowerCase();
          break;
        case 'visibility':
          va = a.visibility || '';
          vb = b.visibility || '';
          break;
        case 'cause':
          va = (a.cause || '').toLowerCase();
          vb = (b.cause || '').toLowerCase();
          break;
        case 'consequence':
          va = (a.consequence || '').toLowerCase();
          vb = (b.consequence || '').toLowerCase();
          break;
        default:
          va = (a.stages || []).join(',');
          vb = (b.stages || []).join(',');
      }
      return (va < vb ? -1 : va > vb ? 1 : 0) * (rlSortDir === 'asc' ? 1 : -1);
    });

  const rlTh = (key: RiskListSortKey, label: string): string => {
    const active = rlSortKey === key;
    return `<th data-sort-rl="${key}" style="cursor:pointer;user-select:none">${esc(label)} <span style="font-size:10px">${active ? (rlSortDir === 'asc' ? '↑' : '↓') : '↕'}</span></th>`;
  };

  // Template de ligne + cases de phases : rendu pur délégué (tri mutable appliqué
  // ici avant l'appel).
  const buildRows = (): string =>
    sortRisks(risks)
      .map((r) => buildStdRiskRow(r, canWrite))
      .join('');

  const rows = buildRows();

  const stageCheckboxes = buildStageCheckboxes(stages);

  const tableHtml = risks.length
    ? `<div class="table-wrap"><table id="std-risks-table">
        <thead id="std-risks-thead"><tr>
          <th style="width:32px"><input type="checkbox" id="chk-select-all-std" title="${esc(t('risk_lists.col.select_all_title'))}"></th>
          ${rlTh('category', t('risk_lists.col.category'))}${rlTh('stages', t('vocabulary.stage_plural'))}${rlTh('visibility', t('risk_lists.col.visibility'))}
          ${rlTh('cause', t('risk_lists.col.cause'))}${rlTh('consequence', t('risk_lists.col.consequence'))}<th></th>
        </tr></thead>
        <tbody id="std-risks-tbody">${rows}</tbody>
      </table></div>`
    : `<div class="empty">${esc(t('risk_lists.empty'))}</div>`;

  setContent(`
    <div class="card" style="margin-bottom:12px">
      <p style="font-size:13px;color:var(--text2)">${esc(listData.description || t('risk_lists.no_description'))}</p>
    </div>
    <div class="card" id="bulk-bar" style="margin-bottom:12px;display:none;background:var(--accent-bg);border-color:var(--accent)">
      <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
        <span id="bulk-count" style="font-size:13px;font-weight:600;color:var(--accent)">${esc(t('risk_lists.bulk.count', { n: 0 }))}</span>
        <span style="font-size:12px;color:var(--text2)">${esc(t('risk_lists.bulk.assign_label'))}</span>
        <div id="bulk-stage-checks">${stageCheckboxes}</div>
        <button class="btn btn-primary btn-sm" id="btn-bulk-apply">${esc(t('risk_lists.bulk.btn.apply'))}</button>
        <button class="btn btn-neutral btn-sm" id="btn-bulk-cancel">${esc(t('common.cancel'))}</button>
      </div>
    </div>
    <div class="card"><div id="std-risks-container">${tableHtml}</div></div>`);

  // Barre d'affectation en masse (tout sélectionner, compteur, Appliquer,
  // Annuler) → page-risklists-bulk-bar.
  wireBulkStageBar(lid, () => {
    void pages.adminRiskListDetail?.({ id: String(lid) });
  });

  document.getElementById('btn-add-std-risk')?.addEventListener('click', () =>
    showStdRiskModal(lid, null, stages, listData.risks, () => {
      void pages.adminRiskListDetail?.({ id: String(lid) });
    }),
  );

  document.getElementById('std-risks-table')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const sortTh = target.closest<HTMLElement>('[data-sort-rl]');
    if (sortTh) {
      const key = sortTh.dataset['sortRl'] as RiskListSortKey;
      if (rlSortKey === key) rlSortDir = rlSortDir === 'asc' ? 'desc' : 'asc';
      else {
        rlSortKey = key;
        rlSortDir = 'asc';
      }
      const tbody = document.getElementById('std-risks-tbody');
      if (tbody) tbody.innerHTML = buildRows();
      document.querySelectorAll<HTMLElement>('[data-sort-rl]').forEach((h) => {
        const a = h.dataset['sortRl'] === rlSortKey;
        const span = h.querySelector('span');
        if (span) span.textContent = a ? (rlSortDir === 'asc' ? '↑' : '↓') : '↕';
      });
    }
  });

  const tbody = document.getElementById('std-risks-tbody');
  if (tbody) {
    tbody.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const clickCell = target.closest<HTMLElement>('[data-edit-std-click]');
      if (clickCell && !target.closest('button') && !target.closest('input')) {
        const rid = parseInt(clickCell.dataset['editStdClick'] || '0');
        const risk = listData.risks.find((r) => r.id === rid);
        if (risk)
          showStdRiskModal(lid, risk, stages, listData.risks, () => {
            void pages.adminRiskListDetail?.({ id: String(lid) });
          });
        return;
      }
      const editBtn = target.closest<HTMLElement>('[data-edit-std]');
      if (editBtn) {
        const rid = parseInt(editBtn.dataset['editStd'] || '0');
        const risk = listData.risks.find((r) => r.id === rid);
        if (risk)
          showStdRiskModal(lid, risk, stages, listData.risks, () => {
            void pages.adminRiskListDetail?.({ id: String(lid) });
          });
        return;
      }
      const delBtn = target.closest<HTMLElement>('[data-del-std]');
      if (delBtn) {
        const rid = parseInt(delBtn.dataset['delStd'] || '0');
        openModal(
          t('risk_lists.modal.delete.title', { object: t('vocabulary.risk') }),
          `<p>${esc(t('risk_lists.modal.delete.message'))}</p>`,
          async () => {
            try {
              await api.deleteStdRisk(lid, rid);
              closeModal();
              toast(t('risk_lists.toast.deleted'));
              void pages.adminRiskListDetail?.({ id: String(lid) });
            } catch (err: unknown) {
              const msg = err instanceof Error ? err.message : t('common.error');
              toast(msg, 'error');
            }
          },
          t('common.delete'),
        );
      }
    });
  }
}

function showStdRiskModal(
  lid: number,
  risk: StdRisk | null,
  stages: ReadonlyArray<StageRow>,
  _existing: ReadonlyArray<StdRisk>,
  onDone: () => void,
): void {
  const isNew = !risk;
  const r: StdRisk = risk || {
    id: 0,
    stages: [],
    category: '',
    visibility: 'publique',
    cause: '',
    consequence: '',
  };
  const stageChecks = stages
    .map(
      (s) => `
    <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
      <input type="checkbox" name="std-stages" value="${esc(s.name)}" ${(r.stages || []).includes(s.name) ? 'checked' : ''}>
      ${esc(s.name)}
    </label>`,
    )
    .join('');
  const body = `
    <div class="form-group"><label>${esc(t('risk_lists.std_modal.field.cause'))}</label><textarea id="sr-cause" rows="2" placeholder="${esc(t('risk_lists.std_modal.field.cause.placeholder'))}">${esc(r.cause || '')}</textarea></div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('risk_lists.std_modal.field.consequence'))}</label><textarea id="sr-consequence" rows="2" placeholder="${esc(t('risk_lists.std_modal.field.consequence.placeholder'))}">${esc(r.consequence || '')}</textarea></div>
    <div class="form-row" style="margin-top:8px">
      <div class="form-group"><label>${esc(t('risk_lists.std_modal.field.category'))}</label><input type="text" id="sr-category" value="${esc(r.category || '')}" placeholder="${esc(t('risk_lists.std_modal.field.category.placeholder'))}"></div>
      <div class="form-group"><label>${esc(t('risk_lists.std_modal.field.visibility'))}</label>
        <select id="sr-visibility">
          <option value="publique" ${r.visibility === 'publique' ? 'selected' : ''}>${esc(t('risk_lists.std_modal.visibility.public'))}</option>
          <option value="privée"   ${r.visibility === 'privée' ? 'selected' : ''}>${esc(t('risk_lists.std_modal.visibility.private'))}</option>
        </select></div>
    </div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('risk_lists.std_modal.field.stages', { plural: t('vocabulary.stage_plural') }))}</label><div>${stageChecks}</div></div>`;
  openModal(
    isNew
      ? t('risk_lists.std_modal.create.title', { object: t('vocabulary.risk') })
      : t('risk_lists.std_modal.update.title', { object: t('vocabulary.risk') }),
    body,
    async () => {
      const checkedStages = Array.from(
        document.querySelectorAll<HTMLInputElement>('input[name="std-stages"]:checked'),
      ).map((c) => c.value);
      const causeEl = document.getElementById('sr-cause') as HTMLTextAreaElement | null;
      const consequenceEl = document.getElementById('sr-consequence') as HTMLTextAreaElement | null;
      const categoryEl = document.getElementById('sr-category') as HTMLInputElement | null;
      const visibilityEl = document.getElementById('sr-visibility') as HTMLSelectElement | null;
      const data = {
        cause: causeEl?.value ?? '',
        consequence: consequenceEl?.value ?? '',
        category: categoryEl?.value ?? '',
        visibility: visibilityEl?.value ?? 'publique',
        stages: checkedStages,
      };
      try {
        if (isNew) await api.createStdRisk(lid, data);
        else if (risk) await api.updateStdRisk(lid, risk.id, data);
        closeModal();
        toast(
          isNew ? t('risk_lists.std_modal.toast.created') : t('risk_lists.std_modal.toast.updated'),
        );
        onDone();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('common.add_short') : t('common.save'),
  );
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.renderRiskListDetail = renderRiskListDetail;
window.showStdRiskModal = showStdRiskModal;

export {};
